<template>
  <div class="mapping-page">
    <section class="mapping-hero">
      <div>
        <span class="eyebrow">SUPER ADMIN CONTROL</span>
        <h1>Management Mapping</h1>
        <p>Atur company yang dapat diakses setiap user E-Balance dari satu tempat.</p>
      </div>
      <div class="hero-icon">
        <feather-icon icon="GitMergeIcon" svgClasses="w-8 h-8" />
      </div>
    </section>

    <div class="summary-grid">
      <article class="summary-card summary-indigo">
        <span>User terkelola</span>
        <strong>{{ users.length }}</strong>
        <small>Super admin tidak memerlukan mapping</small>
      </article>
      <article class="summary-card summary-cyan">
        <span>Total company</span>
        <strong>{{ companies.length }}</strong>
        <small>Company tersedia di E-Balance</small>
      </article>
      <article class="summary-card summary-emerald">
        <span>Akses aktif</span>
        <strong>{{ assignmentCount }}</strong>
        <small>Relasi user dan company</small>
      </article>
    </div>

    <section class="access-panel">
      <div class="panel-header">
        <div>
          <h3>Akses user ke company</h3>
          <p>Pilih satu atau beberapa company, lalu simpan pada user terkait.</p>
        </div>
        <div class="list-filters">
          <div class="search-box">
            <feather-icon icon="SearchIcon" svgClasses="w-4 h-4" />
            <input v-model.trim="search" type="search" placeholder="Cari nama atau email...">
          </div>
          <select v-model="companyFilter" class="filter-select">
            <option value="all">Semua company</option>
            <option v-for="company in companies" :key="`filter-${company.id}`" :value="String(company.id)">{{ company.name }}</option>
          </select>
          <select v-model="accessFilter" class="filter-select">
            <option value="all">Semua status</option>
            <option value="mapped">Sudah memiliki akses</option>
            <option value="unmapped">Belum memiliki akses</option>
          </select>
        </div>
      </div>

      <div class="list-summary">
        <span>Menampilkan <strong>{{ filteredUsers.length }}</strong> dari {{ users.length }} user</span>
        <span><i></i> Perubahan disimpan per user</span>
      </div>

      <div v-if="loading" class="empty-state">Memuat data mapping...</div>
      <div v-else-if="filteredUsers.length === 0" class="empty-state">
        Tidak ada user yang sesuai dengan pencarian.
      </div>
      <div v-else class="access-table-wrap">
        <table class="access-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Akses Company</th>
              <th>Status</th>
              <th class="action-column">Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in filteredUsers" :key="user.id" :class="{ 'row-dirty': isDirty(user) }">
              <td>
                <div class="table-user">
                  <div class="avatar compact">{{ initials(user.name) }}</div>
                  <div><strong>{{ user.name }}</strong><span>{{ user.email }}</span></div>
                </div>
              </td>
              <td><span class="role-badge">{{ roleLabel(user.role_id) }}</span></td>
              <td class="company-column">
                <div v-if="selections[user.id].length" class="company-list-inline">
                  <span v-for="companyID in selections[user.id].slice(0, 3)" :key="`${user.id}-${companyID}`">{{ companyName(companyID) }}</span>
                  <strong v-if="selections[user.id].length > 3">+{{ selections[user.id].length - 3 }}</strong>
                </div>
                <small v-else>Belum mempunyai akses company</small>
              </td>
              <td>
                <span v-if="isDirty(user)" class="mapping-status pending">Belum disimpan</span>
                <span v-else-if="selections[user.id].length" class="mapping-status active">Aktif</span>
                <span v-else class="mapping-status empty">Tanpa akses</span>
              </td>
              <td class="action-column">
                <div class="table-actions">
                  <vs-button size="small" type="border" color="primary" @click="openAccessManager(user)">Kelola Akses</vs-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <vs-popup classContent="access-manager-popup" :title="managedUser ? `Kelola Akses - ${managedUser.name}` : 'Kelola Akses'" :active.sync="accessDialogActive">
      <div v-if="managedUser" class="access-manager">
        <div class="managed-user-summary">
          <div class="avatar compact">{{ initials(managedUser.name) }}</div>
          <div><strong>{{ managedUser.name }}</strong><span>{{ managedUser.email }}</span></div>
        </div>
        <p>Pilih company yang boleh diakses user ini. Bisa satu, dua, tiga, atau kombinasi lainnya.</p>
        <div class="popup-search">
          <feather-icon icon="SearchIcon" svgClasses="w-4 h-4" />
          <input v-model.trim="companyManageSearch" type="search" placeholder="Cari company...">
        </div>
        <div class="company-checklist">
          <label v-for="company in filteredManageCompanies" :key="`manage-${company.id}`" class="company-check-row">
            <vs-checkbox v-model="managedCompanyIDs" :vs-value="Number(company.id)" />
            <div><strong>{{ company.name }}</strong><span>{{ company.npwp || 'NPWP tidak tersedia' }}</span></div>
          </label>
        </div>
        <div class="selection-total">{{ managedCompanyIDs.length }} company dipilih</div>
        <div class="popup-actions">
          <vs-button type="border" color="grey" @click="accessDialogActive = false">Batal</vs-button>
          <vs-button color="primary" :disabled="savingUserId === managedUser.id" @click="saveManagedAccess">{{ savingUserId === managedUser.id ? 'Menyimpan...' : 'Simpan Akses' }}</vs-button>
        </div>
      </div>
    </vs-popup>

    <div class="integration-heading">
      <div>
        <span class="eyebrow dark">CASHFLOW IDENTITY</span>
        <h2>Mapping E-Balance dan Cashflow</h2>
        <p>Hubungkan company dan user E-Balance dengan identitas native Cashflow.</p>
      </div>
      <span class="integration-badge">Khusus super admin</span>
    </div>

    <vx-card class="mapping-panel mb-8">
      <div class="panel-header">
        <div><h3>Mapping Company</h3><p>Pasangkan company E-Balance dengan company Cashflow yang sesuai.</p></div>
      </div>
      <div v-if="cashflowLoading" class="empty-state">Memuat mapping Cashflow...</div>
      <div v-else class="integration-list">
        <div v-for="company in companies" :key="`company-${company.id}`" class="integration-row">
          <div class="source-identity"><span>E-BALANCE</span><strong>{{ company.name }}</strong></div>
          <feather-icon icon="ArrowRightIcon" svgClasses="w-5 h-5 mapping-arrow" />
          <select v-model="cashflowCompanySelections[company.id]" class="mapping-select">
            <option :value="null">Belum terhubung</option>
            <option v-for="target in cashflowCompanies" :key="target.id" :value="target.id">{{ target.name }}</option>
          </select>
          <vs-button size="small" @click="saveCashflowCompany(company.id)">Simpan</vs-button>
          <vs-button size="small" type="border" color="danger" @click="removeCashflowCompany(company.id)">Lepas</vs-button>
        </div>
      </div>
    </vx-card>

    <vx-card class="mapping-panel">
      <div class="panel-header">
        <div><h3>Mapping User Cashflow</h3><p>Hubungkan user E-Balance dengan employee pada company Cashflow yang sudah dipetakan.</p></div>
      </div>
      <div class="cashflow-user-form">
        <div>
          <label>Company E-Balance</label>
          <select v-model="cashflowUserForm.companyId" class="mapping-select"><option :value="null">Pilih company</option><option v-for="company in mappedCompanies" :key="company.id" :value="company.id">{{ company.name }}</option></select>
        </div>
        <div>
          <label>User E-Balance</label>
          <select v-model="cashflowUserForm.userId" class="mapping-select"><option :value="null">Pilih user</option><option v-for="user in availableBalanceUsers" :key="user.id" :value="user.id">{{ user.name }} — {{ user.email }}</option></select>
        </div>
        <div>
          <label>Employee Cashflow</label>
          <select v-model="cashflowUserForm.employeeId" class="mapping-select"><option :value="null">Pilih employee</option><option v-for="employee in availableCashflowEmployees" :key="employee.id" :value="employee.id">{{ employee.name }} — {{ employee.email }}</option></select>
        </div>
        <vs-button @click="saveCashflowUser">Simpan mapping</vs-button>
      </div>
      <div class="mapped-users">
        <div v-for="mapping in cashflowUserMappings" :key="`${mapping.balance_company_id}-${mapping.balance_user_id}`" class="mapped-user-row">
          <span>{{ balanceCompanyName(mapping.balance_company_id) }}</span>
          <feather-icon icon="ArrowRightIcon" svgClasses="w-4 h-4" />
          <span>{{ balanceUserName(mapping.balance_user_id) }}</span>
          <feather-icon icon="ArrowRightIcon" svgClasses="w-4 h-4" />
          <strong>{{ employeeName(mapping.cashflow_employee_id) }}</strong>
          <vs-button size="small" type="border" color="danger" @click="removeCashflowUser(mapping)">Hapus</vs-button>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import axios from '@/axios.js'

export default {
  data () {
    return {
      users: [],
      companies: [],
      selections: {},
      initialSelections: {},
      cashflowCompanies: [],
      cashflowCompanyMappings: [],
      cashflowCompanySelections: {},
      cashflowEmployees: [],
      cashflowUserMappings: [],
      cashflowUserForm: { companyId: null, userId: null, employeeId: null },
      search: '',
      companyFilter: 'all',
      accessFilter: 'all',
      accessDialogActive: false,
      managedUser: null,
      managedCompanyIDs: [],
      companyManageSearch: '',
      loading: false,
      cashflowLoading: false,
      savingUserId: null
    }
  },
  computed: {
    filteredUsers () {
      const keyword = this.search.toLowerCase()
      return this.users.filter(user => {
        const matchesKeyword = !keyword || `${user.name} ${user.email}`.toLowerCase().includes(keyword)
        const selected = this.selections[user.id] || []
        const matchesCompany = this.companyFilter === 'all' || selected.includes(Number(this.companyFilter))
        const matchesStatus = this.accessFilter === 'all' ||
          (this.accessFilter === 'mapped' && selected.length > 0) ||
          (this.accessFilter === 'unmapped' && selected.length === 0)
        return matchesKeyword && matchesCompany && matchesStatus
      })
    },
    assignmentCount () {
      return Object.keys(this.selections).reduce((total, userID) => total + this.selections[userID].length, 0)
    },
    filteredManageCompanies () {
      const keyword = this.companyManageSearch.toLowerCase()
      if (!keyword) return this.companies
      return this.companies.filter(company => `${company.name} ${company.npwp || ''}`.toLowerCase().includes(keyword))
    },
    mappedCompanies () {
      const ids = new Set(this.cashflowCompanyMappings.map(mapping => Number(mapping.balance_company_id)))
      return this.companies.filter(company => ids.has(Number(company.id)))
    },
    availableBalanceUsers () {
      if (!this.cashflowUserForm.companyId) return []
      return this.users.filter(user => user.companies.some(company => Number(company.id) === Number(this.cashflowUserForm.companyId)))
    },
    availableCashflowEmployees () {
      const mapping = this.cashflowCompanyMappings.find(item => Number(item.balance_company_id) === Number(this.cashflowUserForm.companyId))
      if (!mapping) return []
      return this.cashflowEmployees.filter(employee => employee.company_id === mapping.cashflow_company_id)
    }
  },
  watch: {
    'cashflowUserForm.companyId' () {
      this.cashflowUserForm.userId = null
      this.cashflowUserForm.employeeId = null
    }
  },
  created () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      this.cashflowLoading = true
      await Promise.all([this.loadUserCompanyMappings(), this.loadCashflowMappings()])
    },
    async loadUserCompanyMappings () {
      try {
        const response = await axios.get('/user-company-mappings')
        const data = response.data.data
        this.users = data.users
        this.companies = data.companies
        this.selections = this.users.reduce((result, user) => {
          result[user.id] = user.companies.map(company => Number(company.id)).sort((a, b) => a - b)
          return result
        }, {})
        this.initialSelections = JSON.parse(JSON.stringify(this.selections))
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.loading = false
      }
    },
    async loadCashflowMappings () {
      try {
        const response = await axios.get('/cashflow-mappings')
        const cashflowData = response.data.data
        this.cashflowCompanies = cashflowData.cashflow_companies
        this.cashflowCompanyMappings = cashflowData.company_mappings
        this.cashflowEmployees = cashflowData.cashflow_employees
        this.cashflowUserMappings = cashflowData.user_mappings
        this.cashflowCompanySelections = cashflowData.balance_companies.reduce((result, company) => {
          const mapping = this.cashflowCompanyMappings.find(item => Number(item.balance_company_id) === Number(company.id))
          result[company.id] = mapping ? mapping.cashflow_company_id : null
          return result
        }, {})
      } catch (error) {
        this.$vs.notify({ color: 'warning', title: 'Mapping Cashflow', text: 'Mapping Cashflow tidak dapat dimuat, tetapi list akses user-company tetap tersedia.' })
      } finally {
        this.cashflowLoading = false
      }
    },
    async save (user) {
      this.savingUserId = user.id
      try {
        await axios.put(`/user-company-mappings/${user.id}`, {
          company_ids: this.selections[user.id]
        })
        const selectedIDs = [...this.selections[user.id]].sort((a, b) => a - b)
        this.$set(this.selections, user.id, selectedIDs)
        this.$set(this.initialSelections, user.id, [...selectedIDs])
        user.companies = this.companies.filter(company => selectedIDs.includes(Number(company.id)))
        this.$vs.notify({ color: 'success', title: 'Akses diperbarui', text: `Mapping ${user.name} berhasil disimpan.` })
        return true
      } catch (error) {
        this.notifyError(error)
        return false
      } finally {
        this.savingUserId = null
      }
    },
    isDirty (user) {
      const selected = [...(this.selections[user.id] || [])].sort((a, b) => a - b)
      const initial = [...(this.initialSelections[user.id] || [])].sort((a, b) => a - b)
      return JSON.stringify(selected) !== JSON.stringify(initial)
    },
    accessSummary (userID) {
      const total = (this.selections[userID] || []).length
      return total === 0 ? 'Belum mempunyai akses company' : `${total} company dipilih`
    },
    initials (name) {
      return (name || '?').split(' ').slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase()
    },
    roleLabel (roleID) {
      return Number(roleID) === 1 ? 'Administrator' : 'User'
    },
    companyName (companyID) {
      const company = this.companies.find(item => Number(item.id) === Number(companyID))
      return company ? company.name : companyID
    },
    openAccessManager (user) {
      this.managedUser = user
      this.managedCompanyIDs = [...(this.selections[user.id] || [])]
      this.companyManageSearch = ''
      this.accessDialogActive = true
    },
    async saveManagedAccess () {
      this.$set(this.selections, this.managedUser.id, [...this.managedCompanyIDs])
      const saved = await this.save(this.managedUser)
      if (saved) this.accessDialogActive = false
    },
    async saveCashflowCompany (companyID) {
      const targetID = this.cashflowCompanySelections[companyID]
      if (!targetID) return this.removeCashflowCompany(companyID)
      try {
        await axios.put(`/cashflow-mappings/company/${companyID}`, { cashflow_company_id: targetID })
        this.$vs.notify({ color: 'success', title: 'Mapping tersimpan', text: 'Company E-Balance dan Cashflow berhasil dihubungkan.' })
        await this.load()
      } catch (error) {
        this.notifyError(error)
      }
    },
    async removeCashflowCompany (companyID) {
      try {
        await axios.delete(`/cashflow-mappings/company/${companyID}`)
        this.$vs.notify({ color: 'success', title: 'Mapping dilepas', text: 'Mapping company beserta user terkait telah dihapus.' })
        await this.load()
      } catch (error) {
        this.notifyError(error)
      }
    },
    async saveCashflowUser () {
      const form = this.cashflowUserForm
      if (!form.companyId || !form.userId || !form.employeeId) {
        this.$vs.notify({ color: 'warning', title: 'Data belum lengkap', text: 'Pilih company, user, dan employee Cashflow.' })
        return
      }
      try {
        await axios.put(`/cashflow-mappings/company/${form.companyId}/user/${form.userId}`, { cashflow_employee_id: form.employeeId })
        this.$vs.notify({ color: 'success', title: 'Mapping tersimpan', text: 'User E-Balance dan employee Cashflow berhasil dihubungkan.' })
        this.cashflowUserForm = { companyId: null, userId: null, employeeId: null }
        await this.load()
      } catch (error) {
        this.notifyError(error)
      }
    },
    async removeCashflowUser (mapping) {
      try {
        await axios.delete(`/cashflow-mappings/company/${mapping.balance_company_id}/user/${mapping.balance_user_id}`)
        await this.load()
      } catch (error) {
        this.notifyError(error)
      }
    },
    balanceCompanyName (id) {
      const company = this.companies.find(item => Number(item.id) === Number(id))
      return company ? company.name : id
    },
    balanceUserName (id) {
      const user = this.users.find(item => Number(item.id) === Number(id))
      return user ? user.name : id
    },
    employeeName (id) {
      const employee = this.cashflowEmployees.find(item => item.id === id)
      return employee ? employee.name : id
    },
    notifyError (error) {
      const data = error && error.response && error.response.data
      const errors = data && data.errors
      const firstError = errors && Object.keys(errors).length ? errors[Object.keys(errors)[0]][0] : null
      this.$vs.notify({ color: 'danger', title: 'Gagal', text: firstError || (data && data.message) || 'Data mapping tidak dapat diproses.' })
    }
  }
}
</script>

<style scoped>
.mapping-page { color: var(--app-primary-ink); }
.mapping-hero { align-items: center; background: linear-gradient(135deg, rgba(var(--vs-primary),1) 0%, rgba(var(--vs-primary),.86) 55%, rgba(var(--vs-secondary),1) 100%); border-radius: 20px; box-shadow: 0 18px 40px rgba(var(--vs-primary),.2); color: #fff; display: flex; justify-content: space-between; margin-bottom: 24px; overflow: hidden; padding: 30px 34px; position: relative; }
.mapping-hero::after { background: rgba(255,255,255,.1); border-radius: 50%; content: ''; height: 190px; position: absolute; right: -55px; top: -95px; width: 190px; }
.mapping-hero h1 { color: #fff; font-size: 28px; margin: 6px 0 8px; }
.mapping-hero p { color: rgba(255,255,255,.82); margin: 0; }
.eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 1.8px; }
.hero-icon { align-items: center; background: rgba(255,255,255,.16); border: 1px solid rgba(255,255,255,.25); border-radius: 16px; display: flex; height: 64px; justify-content: center; position: relative; width: 64px; z-index: 1; }
.summary-grid { display: grid; gap: 18px; grid-template-columns: repeat(3, 1fr); margin-bottom: 24px; }
.summary-card { background: #fff; border: 1px solid rgba(var(--vs-primary),.14); border-radius: 16px; box-shadow: 0 8px 26px rgba(var(--vs-primary),.08); display: flex; flex-direction: column; padding: 20px; position: relative; }
.summary-card::before { border-radius: 4px; content: ''; height: 32px; left: 0; position: absolute; top: 22px; width: 4px; }
.summary-card span { color: #64748b; font-size: 13px; }
.summary-card strong { color: #0f172a; font-size: 30px; line-height: 1.2; margin: 6px 0; }
.summary-card small { color: #94a3b8; }
.summary-indigo::before,.summary-emerald::before { background: rgba(var(--vs-primary),1); } .summary-cyan::before { background: rgba(var(--vs-secondary),1); }
.access-panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px; }
.mapping-panel { border-radius: 18px; }
.access-table-wrap { overflow-x: auto; }
.access-table { border-collapse: collapse; min-width: 880px; width: 100%; }
.access-table th { background: #f8fafc; border-bottom: 1px solid var(--app-primary-soft); color: #64748b; font-size: 10px; font-weight: 700; letter-spacing: .6px; padding: 11px 12px; text-align: left; text-transform: uppercase; }
.access-table td { border-bottom: 1px solid #edf2f7; color: #475569; padding: 11px 12px; vertical-align: middle; }
.access-table tbody tr { transition: background .15s ease; }
.access-table tbody tr:hover { background: #fafbff; }
.access-table tbody tr.row-dirty { background: var(--app-secondary-soft); }
.table-user { align-items: center; display: flex; gap: 10px; min-width: 180px; }
.table-user > div:last-child { display: flex; flex-direction: column; min-width: 0; }
.table-user strong { color: #0f172a; font-size: 12px; }
.table-user span { color: #94a3b8; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.avatar.compact { border-radius: 9px; flex-basis: 34px; font-size: 10px; height: 34px; }
.role-badge { background: rgba(var(--vs-primary),.1); border-radius: 99px; color: rgba(var(--vs-primary),1); font-size: 9px; font-weight: 700; padding: 5px 8px; white-space: nowrap; }
.company-column { min-width: 300px; }
.company-column small { color: #94a3b8; display: block; font-size: 9px; margin-top: 4px; }
.company-list-inline { align-items: center; display: flex; flex-wrap: wrap; gap: 5px; }
.company-list-inline span { background: rgba(var(--vs-secondary),.1); border-radius: 99px; color: rgba(var(--vs-secondary),1); font-size: 9px; font-weight: 600; padding: 5px 8px; }
.company-list-inline strong { background: #f1f5f9; border-radius: 99px; color: #64748b; font-size: 9px; padding: 5px 8px; }
.mapping-status { border-radius: 99px; display: inline-block; font-size: 9px; font-weight: 700; padding: 5px 8px; white-space: nowrap; }
.mapping-status.active { background: var(--app-primary-soft); color: var(--app-primary-ink); }
.mapping-status.pending { background: var(--app-secondary-border); color: var(--app-secondary-ink); }
.mapping-status.empty { background: #f1f5f9; color: #64748b; }
.action-column { text-align: right !important; }
.table-actions { align-items: center; display: flex; gap: 5px; justify-content: flex-end; }
.table-actions > button:not(.vs-component) { align-items: center; background: rgba(var(--vs-primary),.1); border: 0; border-radius: 7px; color: rgba(var(--vs-primary),1); cursor: pointer; display: flex; height: 30px; justify-content: center; width: 30px; }
.table-actions > button.danger { background: #fff1f2; color: var(--app-secondary); }
.access-manager { color: #334155; }
.managed-user-summary { align-items: center; background: #f8fafc; border-radius: 10px; display: flex; gap: 10px; margin-bottom: 12px; padding: 11px; }
.managed-user-summary > div:last-child { display: flex; flex-direction: column; }
.managed-user-summary span { color: #94a3b8; font-size: 10px; }
.access-manager > p { color: #64748b; font-size: 11px; margin-bottom: 14px; }
.popup-search { align-items: center; border: 1px solid var(--app-primary-soft); border-radius: 9px; display: flex; margin-bottom: 12px; padding: 0 10px; }
.popup-search input { border: 0; outline: none; padding: 10px 7px; width: 100%; }
.company-checklist { border: 1px solid #e2e8f0; border-radius: 10px; max-height: 320px; overflow-y: auto; }
.company-check-row { align-items: center; border-bottom: 1px solid #edf2f7; cursor: pointer; display: flex; gap: 10px; padding: 11px 12px; }
.company-check-row:last-child { border-bottom: 0; }
.company-check-row:hover { background: #f8fafc; }
.company-check-row > div:last-child { display: flex; flex-direction: column; }
.company-check-row strong { color: #1e293b; font-size: 12px; }
.company-check-row span { color: #94a3b8; font-size: 9px; margin-top: 2px; }
.selection-total { color: rgba(var(--vs-primary),1); font-size: 11px; font-weight: 600; margin-top: 10px; }
.popup-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 18px; }
.panel-header { align-items: center; display: flex; gap: 20px; justify-content: space-between; margin-bottom: 20px; }
.panel-header h3 { color: #0f172a; margin-bottom: 4px; }
.panel-header p { color: #64748b; margin: 0; }
.list-filters { align-items: center; display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-end; }
.search-box { align-items: center; background: #f8fafc; border: 1px solid var(--app-primary-soft); border-radius: 12px; display: flex; min-width: 280px; padding: 0 13px; }
.search-box input { background: transparent; border: 0; color: #0f172a; outline: none; padding: 11px 8px; width: 100%; }
.filter-select { background: #fff; border: 1px solid var(--app-primary-soft); border-radius: 10px; color: #475569; min-height: 40px; padding: 7px 10px; }
.list-summary { align-items: center; background: #f8fafc; border-radius: 10px; color: #64748b; display: flex; font-size: 11px; justify-content: space-between; margin-bottom: 6px; padding: 10px 13px; }
.list-summary span:last-child { align-items: center; display: flex; gap: 6px; }.list-summary i { background: var(--app-primary-ink); border-radius: 50%; height: 6px; width: 6px; }
.user-list { display: flex; flex-direction: column; gap: 14px; padding-top: 12px; }
.user-row { align-items: stretch; background: #fff; border: 1px solid var(--app-primary-soft); border-radius: 14px; box-shadow: 0 5px 16px rgba(30, 64, 175, .05); display: flex; flex-direction: column; gap: 18px; padding: 18px; }
.user-profile { align-items: center; display: flex; gap: 12px; min-width: 0; }
.user-profile > div:last-child { display: flex; flex-direction: column; min-width: 0; }
.user-profile strong { color: #0f172a; }
.user-profile span { color: #64748b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.user-profile small { color: rgba(var(--vs-primary),1); font-weight: 600; margin-top: 3px; }
.avatar { align-items: center; background: linear-gradient(135deg, rgba(var(--vs-primary),.16), rgba(var(--vs-secondary),.16)); border-radius: 13px; color: rgba(var(--vs-primary),1); display: flex; flex: 0 0 44px; font-weight: 700; height: 44px; justify-content: center; }
.company-picker label { color: #475569; display: block; font-size: 12px; font-weight: 600; margin-bottom: 7px; }
.company-picker small { color: #94a3b8; display: block; margin-top: 6px; }
.access-chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 8px; }.access-chips span { background: rgba(var(--vs-secondary),.1); border-radius: 99px; color: rgba(var(--vs-secondary),1); font-size: 9px; font-weight: 600; padding: 4px 8px; }.access-chips em { color: var(--app-secondary); font-size: 10px; font-style: normal; }
.row-actions { align-items: center; border-top: 1px solid #f1f5f9; display: flex; flex-direction: row; gap: 10px; justify-content: flex-end; padding-top: 14px; }.text-action { background: transparent; border: 0; color: rgba(var(--vs-primary),1); cursor: pointer; font-size: 10px; padding: 7px 4px; }.text-action.danger { color: var(--app-secondary); }
.empty-state { color: #64748b; padding: 40px; text-align: center; }
.integration-heading { align-items: center; display: flex; justify-content: space-between; margin: 34px 4px 18px; }
.integration-heading h2 { color: #0f172a; margin: 5px 0; }
.integration-heading p { color: #64748b; margin: 0; }
.eyebrow.dark { color: rgba(var(--vs-primary),1); }
.integration-badge { background: rgba(var(--vs-secondary),.1); border-radius: 99px; color: rgba(var(--vs-secondary),1); font-size: 11px; font-weight: 700; padding: 7px 12px; }
.integration-list { border-top: 1px solid #edf2f7; }
.integration-row { align-items: center; border-bottom: 1px solid #edf2f7; display: grid; gap: 14px; grid-template-columns: minmax(190px, 1fr) auto minmax(220px, 1.2fr) auto auto; padding: 15px 2px; }
.source-identity { display: flex; flex-direction: column; }
.source-identity span { color: rgba(var(--vs-primary),1); font-size: 9px; font-weight: 700; letter-spacing: 1px; }
.source-identity strong { color: #1e293b; margin-top: 3px; }
.mapping-arrow { color: #94a3b8; }
.mapping-select { background: #fff; border: 1px solid var(--app-primary-soft); border-radius: 9px; color: #334155; min-height: 39px; padding: 7px 10px; width: 100%; }
.cashflow-user-form { align-items: end; background: #f8fafc; border: 1px solid var(--app-primary-soft); border-radius: 14px; display: grid; gap: 14px; grid-template-columns: repeat(3, minmax(180px, 1fr)) auto; padding: 16px; }
.cashflow-user-form label { color: #475569; display: block; font-size: 11px; font-weight: 600; margin-bottom: 6px; }
.mapped-users { margin-top: 18px; }
.mapped-user-row { align-items: center; border-bottom: 1px solid #edf2f7; color: #64748b; display: grid; gap: 10px; grid-template-columns: 1fr auto 1fr auto 1fr auto; padding: 12px 4px; }
.mapped-user-row strong { color: #0f172a; }
@media (max-width: 900px) { .summary-grid { grid-template-columns: 1fr; } }
@media (max-width: 900px) { .integration-row, .cashflow-user-form, .mapped-user-row { grid-template-columns: 1fr; } .mapping-arrow { transform: rotate(90deg); } }
@media (max-width: 640px) { .mapping-hero { padding: 24px; } .hero-icon { display: none; } .panel-header, .integration-heading { align-items: stretch; flex-direction: column; gap: 12px; } .list-filters { align-items: stretch; flex-direction: column; }.search-box { min-width: 0; }.filter-select { width: 100%; }.list-summary { align-items: flex-start; flex-direction: column; gap: 6px; }.row-actions { align-items: stretch; flex-direction: column; }.row-actions .text-action { text-align: left; } }
</style>
